'use client';

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Language = 'es' | 'en';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  es: {
    // Header / Nav
    'nav.home': 'Inicio',
    'nav.services': 'Servicios',
    'nav.products': 'Productos',
    'nav.compliance': 'Cumplimiento',
    'nav.contact': 'Contacto',
    'nav.contactUs': 'Contáctenos',

    // Footer
    'footer.rights': '© 2026 MERCALIA Connect Group LLC. Todos los derechos reservados.',
    'footer.tagline': 'Su socio estratégico en el origen · Nuevo México, EE. UU.',
    'footer.verify': 'Verificar credenciales',
    'footer.ca': 'Verificada por ConnectAmericas',
    'footer.privacy': 'Privacidad',
    'footer.terms': 'Términos',

    // Hero Section
    'hero.label': 'Argentina · Brasil · Bolivia · Chile · Paraguay · Perú · Centroamérica',
    'hero.line1': 'De la planta de origen',
    'hero.line2': 'a su depósito.',
    'hero.line3': '',
    'hero.line4': '',
    'hero.description': 'Nuestra especialidad: langostino salvaje argentino (Pleoticus muelleri) y productos del mar patagónicos, de plantas habilitadas para exportar. También abastecemos, por contenedor y desde el origen, cordero patagónico, azúcares de caña, aceite de oliva extra virgen, quinoa y café. Vea la lista completa en Productos.',
    'hero.pronto': '',
    'hero.cta.services': 'Ver Productos',
    'hero.cta.products': 'Verificar credenciales',
    'hero.scroll': 'Desplazar',
    'hero.badge.iso': 'Permiso NOAA IFTP00104977 · vigente a 2027',
    'hero.badge.ofac': 'D-U-N-S® 149902101',
    'hero.badge.aml': 'Trato directo con la planta',
    'hero.badge.llc': 'Verificada por ConnectAmericas',

    // Stats Section
    'stats.label': 'En Números',
    'stats.title': 'Lo que hay detrás de cada oferta',
    'home.permit.title': 'Permiso federal propio para importar productos del mar a los Estados Unidos',
    'home.permit.subtitle': 'International Fisheries Trade Permit — NOAA Fisheries · N.º',
    'home.permit.cta': 'Ver permisos y cumplimiento',

    'stats.0.label': 'Orígenes',
    'stats.0.description': 'Argentina, Brasil, Bolivia, Chile, Paraguay, Perú y Centroamérica, según el producto y la zona de captura o de producción',
    'stats.1.label': 'Líneas de producto de primera mano',
    'stats.1.description': 'Productos del mar · cordero patagónico · aceite de oliva virgen extra · quinoa, chía y sésamo · café · azúcares de caña',
    'stats.2.label': 'Trato directo con el productor',
    'stats.2.description': 'Compramos en la planta, el frigorífico o el ingenio de origen, de primera mano. Sin capas ocultas ni reventa de terceros: de eso respondemos',
    'stats.2.display': 'DIRECTO',

    // Services Overview
    'servicesOverview.label': 'Lo Que Hacemos',
    'servicesOverview.title1': 'Comercio de alimentos,',
    'servicesOverview.title2': 'de Materias Primas',
    'servicesOverview.titleItalic': 'de punta a punta',
    'servicesOverview.allServices': 'Todos los Servicios',
    'servicesOverview.learnMore': 'Saber más',
    'servicesOverview.0.title': 'Compra en origen',
    'servicesOverview.0.description': 'Compramos de primera mano en la planta pesquera, el frigorífico, el ingenio o la bodega, y le vendemos a nuestro nombre: tomamos título de la mercadería y controlamos el conocimiento de embarque. Sin revendedores en el medio.',
    'servicesOverview.1.title': 'Precio y ventanas de origen',
    'servicesOverview.1.description': 'Seguimos precio, temporada y disponibilidad en las plantas con las que trabajamos, y le decimos cuándo comprar y cuándo esperar, antes de cotizar.',
    'servicesOverview.2.title': 'Logística y Operaciones',
    'servicesOverview.2.description': 'Contenedor refrigerado o seco, completo o consolidado, desde puertos de Sudamérica y Centroamérica; documentación y entrega en el puerto de destino o, en los Estados Unidos, en su depósito.',
    'servicesOverview.3.title': 'Cumplimiento y Riesgo',
    'servicesOverview.3.description': 'Verificamos a cada contraparte contra las listas de sanciones internacionales y aplicamos los controles de prevención de lavado y conocimiento del cliente (OFAC, AML y KYC) antes de cada operación.',
    'servicesOverview.4.title': 'Pago y documentos',
    'servicesOverview.4.description': 'Definimos el Incoterm, el instrumento de pago y quién controla el conocimiento de embarque antes de mover la carga. Mercalia mantiene el control documental hasta completar el pago.',
    'servicesOverview.5.title': 'Servicio para exportadores',
    'servicesOverview.5.badge': 'Importador de registro · EE. UU.',
    'servicesOverview.5.description': '¿Exporta alimentos y quiere venderlos en los Estados Unidos? Ofrecemos el servicio de importador de registro para exportadores, con despachante habilitado y presencia en el país. Primer paso: diagnóstico sin cargo.',

    // Commodities Showcase
    'commodities.label': 'Portafolio de Productos',
    'commodities.title1': 'Nuestras',
    'commodities.title2': 'Líneas',
    'commodities.titleItalic': 'Principales',
    'commodities.primaryLabel': 'Especialidad: Langostino Salvaje Argentino · También: Cordero Patagónico · Azúcares de Caña · Aceite de Oliva · Quinoa · Café',
    'commodities.fullPortfolio': 'Portafolio Completo',

    // Why Mercalia
    'why.label': 'Por Qué Elegirnos',
    'why.title1': 'Su Socio Estratégico',
    'why.title2': 'en el Origen',
    'why.titleItalic': 'en el Origen',
    'why.title3': 'de los Alimentos de Sudamérica y Centroamérica',
    'why.description': 'En un mercado con riesgo de contraparte y controles regulatorios estrictos, MERCALIA Connect Group LLC trabaja con reglas claras: verificamos a cada contraparte, documentamos cada paso y le decimos la verdad sobre precio, temporada y disponibilidad.',
    'why.stats.volume': 'Permiso federal propio',
    'why.stats.since': 'NOAA Fisheries · N.º IFTP00104977',
    'why.stats.growth': 'Vigente hasta julio de 2027',
    'why.badge': 'Permiso Federal NOAA Vigente',
    'why.0.title': 'Arquitectura de Cumplimiento Total',
    'why.0.description': 'Antes de cada operación verificamos a la contraparte contra las listas de sanciones internacionales y aplicamos los controles de prevención de lavado y conocimiento del cliente (OFAC, AML y KYC). Eso protege a nuestros clientes de riesgos legales y de reputación. Verificamos a cada cliente nuevo antes de darlo de alta: le pedimos su ficha corporativa y su comprobante de fondos (CIS y POF).',
    'why.1.title': 'Plantas Verificadas en Origen',
    'why.1.description': 'Trato directo con plantas habilitadas para exportar en Sudamérica y Centroamérica. Sin revendedores en el medio: sabemos exactamente de dónde viene su mercadería y cómo se mueve.',
    'why.2.title': 'Precio y ventana de origen',
    'why.2.description': 'Seguimos el precio y la disponibilidad en las plantas con las que trabajamos, y le decimos cuándo conviene comprar y cuándo no. Si la ventana de captura o la zafra no acompañan, se lo decimos antes de cotizar.',
    'why.3.title': 'Un solo interlocutor, de principio a fin',
    'why.3.description': 'La misma persona lleva su operación desde la primera consulta hasta la entrega. No hay derivaciones, ni mesa de ayuda, ni tener que volver a explicar el pedido.',
    'why.4.title': 'Continuidad de temporada',
    'why.4.description': 'Trabajamos con plantas que congelan a bordo y en tierra, lo que define qué presentación hay disponible en cada mes del año. Planificamos su abastecimiento sobre ese calendario real, no sobre una promesa.',

    // Home CTA
    'homeCTA.badge': 'Abiertos a Nuevos Clientes y Proveedores',
    'homeCTA.title1': '¿Busca un Proveedor',
    'homeCTA.titleItalic': 'de Confianza?',
    'homeCTA.description': 'Ya sea que necesite un solo envío a granel o un acuerdo de suministro a largo plazo, estamos listos para estructurar la solución adecuada para su negocio.',
    'homeCTA.cta.start': 'Iniciar una Conversación',
    'homeCTA.cta.services': 'Explorar Servicios',
    'homeCTA.trust.0': 'Consultas Confidenciales',
    'homeCTA.trust.1': 'Sin Compromiso',
    'homeCTA.trust.2': 'Respuesta en 24h',

    // Services Page
    'services.hero.label': 'Lo Que Ofrecemos',
    'services.hero.title1': 'Soluciones Globales',
    'services.hero.title2': 'en Comercio',
    'services.hero.titleItalic': 'Internacional',
    'services.hero.description': 'Compramos de primera mano en Sudamérica y Centroamérica y entregamos donde el comprador lo necesita, desde la primera consulta hasta la entrega final.',
    'services.hero.cta.consult': 'Solicitar cotización',
    'services.hero.cta.products': 'Ver Productos',
    'services.hero.breadcrumb.home': 'Inicio',
    'services.hero.breadcrumb.services': 'Servicios',

    'services.grid.label': 'Servicios Principales',
    'services.grid.title1': 'Abastecimiento de Origen',
    'services.grid.titleItalic': 'y Servicios Comerciales',

    'services.0.title': 'Abastecimiento y Adquisición Internacional',
    'services.0.description': 'Compramos directo en plantas pesqueras, frigoríficos, ingenios y acopios habilitados de Sudamérica y Centroamérica, y le vendemos a nuestro nombre. El contenedor sale de la planta de origen directo hacia usted, sin revendedores en el medio.',
    'services.0.f0': 'Verificación y debida diligencia de proveedores',
    'services.0.f1': 'Certificación de origen',
    'services.0.f2': 'Un solo interlocutor de principio a fin',
    'services.0.f3': 'Cotización FOB y CFR en paralelo',

    'services.1.title': 'Productos del Mar y Cadena de Frío',
    'services.1.description': 'Langostino salvaje argentino y demás especies del Atlántico Sur y del Pacífico. Manejamos la ventana de temporada, la diferencia entre congelado a bordo y congelado en tierra, y la documentación de entrada a los Estados Unidos.',
    'services.1.f0': 'Permiso NOAA IFTP y declaración SIMP',
    'services.1.f1': 'Congelado a bordo o en tierra',
    'services.1.f2': 'Contenedor refrigerado de 40 pies',

    'services.2.title': 'Estructura de Pago y Documentación',
    'services.2.description': 'Definimos la estructura de pago antes de mover la carga: INCOTERM, instrumento y quién controla el conocimiento de embarque. Mercalia mantiene el control documental hasta que el pago está cumplido.',
    'services.2.f0': 'Cotizamos FOB, FCA, CFR y CIF',
    'services.2.f1': 'Pago contra documentos',
    'services.2.f2': 'Juego documental completo de exportación',
    'services.2.f3': 'Entrega en destino DAP y DDP para los Estados Unidos',

    'services.3.title': 'Quinoa, Chía, Sésamo, Aceite de Oliva y Azúcar',
    'services.3.description': 'Quinoa, chía y sésamo; aceite de oliva virgen extra; y azúcares de caña: blanca refinada, crudo y demerara, rubia y mascabo. Orígenes en Sudamérica y Centroamérica, por contenedor según volumen y puerto de destino.',
    'services.3.f0': 'Quinoa, chía y sésamo',
    'services.3.f1': 'Aceite de oliva virgen extra',
    'services.3.f2': 'Azúcares de Brasil, Argentina y Centroamérica',

    'services.4.title': 'Café',
    'services.4.description': 'Café verde y tostado de plantaciones de El Salvador y Brasil, con trazabilidad al productor.',
    'services.4.f0': 'Trazabilidad al productor',
    'services.4.f1': 'Café verde y tostado de El Salvador y Brasil',
    'services.4.f2': 'Distintas presentaciones y empaques',

    'services.5.title': 'Cordero Patagónico',
    'services.5.description': 'Cordero patagónico criado a pasto, de plantas habilitadas por SENASA para exportación de ovino: la zafra va de octubre a enero con pico en noviembre y diciembre, y la temporada se compromete con anticipación. Destinos del cordero: Centroamérica, el Caribe, Europa, Medio Oriente e Israel, entre otros.',
    'services.5.f0': 'Cordero: zafra de octubre a enero, pico en noviembre y diciembre',
    'services.5.f1': 'Halal y Kosher según planta y destino',
    'services.5.f2': 'Carcasas, cortes con hueso y cortes deshuesados',
    'services.5.f3': 'Enfriado o congelado, según destino y tránsito',

    'services.howWeWork.label': 'Nuestro Proceso',
    'services.howWeWork.title1': 'Cómo Ejecutamos',
    'services.howWeWork.titleItalic': 'Cada Transacción',
    'services.howWeWork.description': 'Un proceso estructurado y responsable desde el primer contacto hasta la entrega final — diseñado para minimizar riesgos y optimizar resultados en el comercio internacional de materias primas.',
    'services.step.0.title': 'Consulta Inicial',
    'services.step.0.description': 'Usted envía sus requisitos de materias primas — tipo, volumen, preferencias de origen, plazo de entrega y parámetros presupuestarios.',
    'services.step.1.title': 'Evaluación de Necesidades',
    'services.step.1.description': 'Revisamos sus requisitos y, si hace falta, coordinamos una llamada para aclarar especificaciones, temporada y condiciones del mercado.',
    'services.step.2.title': 'Verificación de Cumplimiento',
    'services.step.2.description': 'Antes de cualquier compromiso comercial verificamos a todas las partes contra las listas de sanciones internacionales y aplicamos los controles de prevención de lavado y conocimiento del cliente (OFAC, AML y KYC).',
    'services.step.3.title': 'Abastecimiento y Cotización',
    'services.step.3.description': 'Verificamos precio y disponibilidad en las plantas con las que trabajamos, confirmamos las especificaciones del producto y le enviamos una oferta formal por escrito.',
    'services.step.4.title': 'Ejecución del Contrato',
    'services.step.4.description': 'Firmamos el contrato con usted (Incoterm, instrumento de pago e inspección) y cerramos la compra en origen a nombre de Mercalia.',
    'services.step.5.title': 'Entrega y Liquidación',
    'services.step.5.description': 'Coordinación logística completa, gestión de documentación y liquidación de pagos. Permanecemos responsables hasta que la materia prima sea entregada y todas las obligaciones cumplidas.',

    'services.cta.title': '¿Listo para Conectar Mercados?',
    'services.cta.description': 'Nuestro equipo está disponible para discutir sus necesidades de abastecimiento, revisar sus requisitos y proponer la solución adecuada — de forma confidencial y sin compromiso. Contáctenos en',
    'services.cta.whatsapp': 'WhatsApp — Argentina, en origen',
    'services.cta.inquiry': 'Enviar Consulta',
    'services.cta.trust.0': 'Consultas Confidenciales',
    'services.cta.trust.1': 'Sin Compromiso',
    'services.cta.trust.2': 'Origen Sudamérica y Centroamérica',

    // Products Page
    'products.hero.label': 'Portafolio de Productos',
    'products.hero.title1': 'Portafolio de',
    'products.hero.titleItalic': 'Primera Mano',
    'products.hero.description': 'Lista completa de productos desde Argentina, Brasil, Bolivia, Chile, Paraguay, Perú y Centroamérica. Nuestra especialidad es el langostino salvaje argentino; el resto lo abastecemos por contenedor y desde el origen. El contenedor sale de la planta de origen directo hacia el comprador, sin revendedores en el medio.',
    'products.hero.cta': 'Solicitar Consulta de Producto',
    'products.hero.breadcrumb.home': 'Inicio',
    'products.hero.breadcrumb.products': 'Productos',
    'products.hero.stat.0.label': 'Líneas de Primera Mano',
    'products.hero.stat.0.sub': 'Del productor al comprador',
    'products.hero.stat.1.label': 'Orígenes',
    'products.hero.stat.1.sub': 'Sudamérica y Centroamérica',
    'products.hero.stat.2.label': 'Formato de Entrega',
    'products.hero.stat.2.sub': 'Contenedor seco y refrigerado',
    'products.hero.stat.3.label': 'Tiempo de Respuesta',
    'products.hero.stat.3.sub': 'Para consultas estándar',

    'products.grid.label': 'Categorías de Productos',
    'products.grid.title1': 'Lo que Abastecemos',
    'products.grid.titleItalic': 'y Comercializamos',
    'products.grid.inquire': 'Consultar sobre esta categoría',
    'products.grid.note': '¿Busca algo que no figura en esta lista?',
    'products.grid.contact': 'Contáctenos',
    'products.grid.noteLink': 'y le decimos con franqueza si podemos abastecerlo.',
    'products.grid.filter.all': 'Todos',

    'products.cat.0.name': 'Productos del Mar',
    'products.cat.0.description': 'Langostino salvaje argentino entero, en colas y pelado; merluza y merluza negra, calamar illex, vieira patagónica, pulpo, pez espada y albacora, centolla, mejillones, salmón, trucha y caviar premium. Congelado a bordo o en tierra según especie y temporada.',
    'products.cat.1.name': 'Cordero Patagónico',
    'products.cat.1.description': 'Criado a pasto, de plantas habilitadas para exportación. Zafra de octubre a enero, con pico en noviembre y diciembre. Destinos: Centroamérica, el Caribe, Europa, Medio Oriente e Israel, entre otros.',
    'products.cat.3.name': 'Café',
    'products.cat.3.description': 'Café verde y tostado de plantaciones de El Salvador y Brasil, con trazabilidad al productor.',
    'products.cat.4.name': 'Azúcares de Caña',
    'products.cat.4.description': 'De ingenios de Brasil, Argentina y Centroamérica. En contenedor seco, big bag o granel de buque; marca privada a pedido.',
    'products.cat.6.name': 'Quinoa, Chía y Sésamo',
    'products.cat.6.description': 'Quinoa blanca convencional de Bolivia, del Altiplano de Oruro, con lote analizado en laboratorio acreditado ISO/IEC 17025: gluten no detectado. Chía negra o blanca de Paraguay, orgánica o convencional. Sésamo blanco natural con cáscara, limpieza Sortex. Por contenedor, con programas por volumen.',

    'products.tag.0': 'Mar',
    'products.tag.1': 'Cordero',
    'products.tag.3': 'Café',
    'products.tag.4': 'Azúcar',
    'products.tag.6': 'Quinoa, Chía y Sésamo',
    'products.cat.7.name': 'Aceite de Oliva Virgen Extra',
    'products.cat.7.description': 'Aceite de oliva virgen extra a granel, de planta con HACCP certificado, y envasado con marca de la finca o marca propia.',
    'products.tag.7': 'Aceite de oliva',
    'products.grid.credits': 'Imágenes ilustrativas.',

    'products.sourcing.label': 'Orígenes y Puertos de Embarque',
    'products.sourcing.title1': 'De Dónde Sale',
    'products.sourcing.titleItalic': 'la Carga',
    'products.sourcing.delivery.label': 'Formatos de Embarque',
    'products.sourcing.deliveryReach': 'Puertos de Embarque',
    'products.sourcing.deliveryTitle': 'Atlántico Sur y Pacífico',
    // Alcance global + Importador de registro (Cumplimiento)
    'reach.label': 'Alcance Comercial',
    'reach.title': 'El origen es sudamericano y centroamericano. El destino lo define el comprador.',
    'reach.lead': 'Mercalia trabaja con compradores de cualquier mercado. Embarcamos desde puertos de Sudamérica y Centroamérica hacia los siguientes mercados de destino.',
    'reach.market.0': 'Unión Europea y Reino Unido',
    'reach.market.1': 'Emiratos Árabes Unidos y el Golfo',
    'reach.market.2': 'Asia',
    'reach.market.3': 'África',
    'reach.market.4': 'América del Norte',
    'reach.market.5': 'Resto del continente americano',
    'reach.quoting': 'Cotizamos en FOB, FCA, CFR y CIF para cualquier destino, en contenedor completo o consolidado, con la documentación de exportación y el control del conocimiento de embarque en cabeza de Mercalia.',
    'reach.caveat': 'La habilitación sanitaria y los requisitos de acceso dependen del producto y del mercado de destino, y se confirman por establecimiento antes de cotizar en firme.',

    'ior.label': 'Y para los Estados Unidos',
    'ior.title': 'Algo que la mayoría de los exportadores en origen no ofrece',
    'ior.p1': 'Mercalia Connect Group LLC es una sociedad constituida en los Estados Unidos, con domicilio en Nuevo México y número D-U-N-S® propio. Además del abastecimiento en origen, ofrecemos el servicio de importador de registro para exportadores, con despachante habilitado y presencia en el país.',
    'ior.p2': 'En la práctica, un comprador estadounidense puede abastecerse directamente en origen y recibir la mercadería en su depósito.',
    'ior.dap.badge': 'Recomendado',
    'ior.dap.title': 'Entrega en el lugar convenido',
    'ior.dap.body': 'Mercalia gestiona el despacho de importación por cuenta y orden del comprador. Los derechos, tasas y recargos vigentes a la fecha de entrada se refacturan al costo, contra comprobante y sin recargo.',
    'ior.ddp.title': 'Entrega con derechos e impuestos incluidos',
    'ior.ddp.body': 'El precio incluye derechos e impuestos de importación. Sujeto a confirmación por operación.',
    'ior.recommendation.a': 'Recomendamos DAP. Bajo DDP los derechos quedan dentro de un precio cerrado, y ese precio tiene que contemplar la variación arancelaria entre la cotización y la fecha de entrada. Bajo DAP el comprador',
    'ior.recommendation.b': 'paga exactamente lo que liquidó la aduana',
    'ior.recommendation.c': 'y ve el comprobante, sin pagar prima por un riesgo que nadie controla.',

    // Servicio para exportadores: importador de registro (texto maestro aprobado por José el 9-oct-2026)
    'xsvc.label': 'Servicio para exportadores',
    'xsvc.title': 'Importador de registro en Estados Unidos',
    'xsvc.meta': 'Mercalia Connect Group LLC · empresa estadounidense · Nuevo México, EE.UU.',
    'xsvc.banner.lead': '¿Exporta alimentos y quiere vender en Estados Unidos? Ofrecemos el servicio de importador de registro para exportadores, con despachante habilitado y presencia en el país.',
    'xsvc.banner.cta': 'Ver el servicio',
    'xsvc.why.title': '¿Por qué lo necesita?',
    'xsvc.why.body': 'Para vender alimentos en Estados Unidos no alcanza con tener un buen producto y un comprador. Alguien tiene que figurar ante la Aduana (CBP) y ante la FDA como importador de la mercadería, con fianza aduanera, despachante habilitado y presencia en el país. La mayoría de las empresas exportadoras no tiene esa estructura. Mercalia Connect Group LLC, empresa estadounidense, ofrece ese servicio a exportadores.',
    'xsvc.resp.title': 'Qué responsabilidad asume el importador de registro',
    'xsvc.resp.body': 'Es quien responde ante el Estado de Estados Unidos por toda la importación: que la mercadería esté bien declarada y clasificada, que se paguen los aranceles y que cumpla las normas de ingreso. Si algo falla, las multas, observaciones, retenciones o devoluciones recaen sobre él. Por eso debe constituir una fianza aduanera que garantiza esas obligaciones ante el gobierno. Es la responsabilidad que la ley de Estados Unidos impone al importador de registro.',
    'xsvc.do.title': 'Qué hacemos por usted',
    'xsvc.do.1.title': 'Su producto y su planta, en regla',
    'xsvc.do.1.body': 'Revisamos y ordenamos todo lo que Estados Unidos exige antes de embarcar: registro de la planta ante la FDA, identificación de la empresa (número DUNS), etiquetado, origen, documentación del producto y permisos específicos según el rubro.',
    'xsvc.do.2.title': 'El ingreso y la entrega',
    'xsvc.do.2.body': 'Figuramos como importador de registro, coordinamos el despacho con agente de aduana licenciado, presentamos el aviso previo a la FDA, gestionamos los permisos aduaneros y acompañamos la mercadería hasta que llega a su cliente en Estados Unidos.',
    'xsvc.how.title': 'Cómo trabajamos',
    'xsvc.how.body': 'Cada operación sigue un procedimiento con controles en cada etapa: si un requisito no está cumplido, no avanzamos. Preferimos resolver en origen antes que tener un problema en el puerto.',
    'xsvc.diag.title': 'Primer paso: diagnóstico sin cargo',
    'xsvc.diag.body': 'Le decimos si su producto y su documentación están en condiciones de entrar a Estados Unidos, y qué falta si no lo están.',
    'xsvc.diag.cta': 'Pida su diagnóstico',
    'xsvc.diag.subject': 'Diagnóstico sin cargo — importador de registro en EE.UU.',
    'xsvc.downloads': 'Descargas',
    'xsvc.dl.sheet': 'Hoja del servicio (PDF, español)',
    'xsvc.dl.sheetAlt': 'Service sheet (PDF, English)',
    'xsvc.dl.catalog': 'Catálogo de productos Rev. 7 (PDF, inglés-español)',
    'xsvc.note': 'Información comercial. Las condiciones del servicio se fijan por escrito en cada contrato.',

    'products.sourcing.incoterms': 'INCOTERMS 2020 que Cotizamos',
    'products.sourcing.incoterms.note': 'FOB, FCA, CFR y CIF a cualquier destino. DAP y DDP aplican a entregas dentro de los Estados Unidos, donde Mercalia está en condiciones de actuar como importador de registro. DDP sujeto a confirmación por operación.',
    'products.sourcing.region.0': 'Argentina',
    'products.sourcing.region.0.desc': 'Productos del mar, cordero patagónico, azúcar y aceite de oliva',
    'products.sourcing.region.0.ports': 'Puerto de embarque a confirmar por operación',
    'products.sourcing.region.1': 'Brasil',
    'products.sourcing.region.1.desc': 'Azúcar y café',
    'products.sourcing.region.1.ports': 'Puerto de embarque a confirmar por operación',
    'products.sourcing.region.2': 'Chile',
    'products.sourcing.region.2.desc': 'Productos del mar del Pacífico, según especie y temporada',
    'products.sourcing.region.2.ports': 'Puerto de embarque a confirmar por operación',
    'products.sourcing.region.3': 'Perú',
    'products.sourcing.region.3.desc': 'Productos del mar del Pacífico, según especie y temporada',
    'products.sourcing.region.3.ports': 'Puerto de embarque a confirmar por operación',
    'products.sourcing.region.4': 'Bolivia',
    'products.sourcing.region.4.desc': 'Quinoa blanca del Altiplano de Oruro',
    'products.sourcing.region.4.ports': 'Arica (Chile)',
    'products.sourcing.region.5': 'Centroamérica',
    'products.sourcing.region.5.desc': 'Azúcar de caña y café de El Salvador',
    'products.sourcing.region.5.ports': 'Puerto de embarque a confirmar por operación',
    'products.sourcing.region.6': 'Paraguay',
    'products.sourcing.region.6.desc': 'Chía negra y blanca, orgánica y convencional',
    'products.sourcing.region.6.ports': 'Asunción',
    'products.sourcing.delivery.0': 'Contenedor Refrigerado',
    'products.sourcing.delivery.0.desc': '40 pies, congelados',
    'products.sourcing.delivery.1': 'Contenedor Seco',
    'products.sourcing.delivery.1.desc': '20 y 40 pies, FCL',
    'products.sourcing.delivery.2': 'Consolidado',
    'products.sourcing.delivery.2.desc': 'LCL, pedidos chicos',
    'products.sourcing.delivery.3': 'Granel',
    'products.sourcing.delivery.3.desc': 'A pedido, según volumen',

    'products.cta.badge': 'Abastecimiento a Medida',
    'products.cta.title1': '¿Necesita un Producto',
    'products.cta.titleItalic': 'en Particular?',
    'products.cta.description': 'Envíe su especificación — producto, presentación, volumen, origen y condiciones de entrega — y le respondemos con las opciones de abastecimiento que tengamos.',
    'products.cta.item.0.label': 'Consulta de Producto',
    'products.cta.item.0.desc': 'Producto, volumen y especificaciones',
    'products.cta.item.1.label': 'Ficha Técnica',
    'products.cta.item.1.desc': 'Especies, presentaciones y origen',
    'products.cta.item.2.label': 'Acuerdo de Temporada',
    'products.cta.item.2.desc': 'Volumen comprometido para la próxima zafra',

    // Contact Page
    'contact.hero.label': 'Póngase en Contacto',
    'contact.hero.title1': 'Iniciemos una',
    'contact.hero.titleItalic': 'Conversación',
    'contact.hero.description': 'Ya sea que busque alimentos en origen, una cotización puesta en su depósito o nuestro servicio de importador de registro, le respondemos dentro de las 24 horas.',
    'contact.hero.breadcrumb.home': 'Inicio',
    'contact.hero.breadcrumb.contact': 'Contacto',
    'contact.hero.stat.0.label': 'Tiempo de respuesta',
    'contact.hero.stat.1.label': 'Ubicaciones',
    'contact.hero.stat.2.label': 'Idiomas de atención',
    'contact.hero.stat.3.label': 'Permiso federal NOAA propio',

    'contact.form.title': 'Enviar una Consulta',
    'contact.form.name': 'Nombre Completo',
    'contact.form.namePlaceholder': 'Nombre',
    'contact.form.company': 'Empresa',
    'contact.form.commodity': 'Producto de interés',
    'contact.form.commodityPlaceholder': 'Seleccione una línea de producto…',
    'contact.form.message': 'Mensaje',
    'contact.form.messagePlaceholder': 'Describa sus necesidades de abastecimiento, volúmenes, mercados objetivo o cualquier requisito específico…',
    'contact.form.privacy': 'Al enviar este formulario acepta nuestra política de privacidad. Su información se usa sólo para responder su consulta y nunca se comparte con terceros.',
    'contact.form.submit': 'Enviar Consulta',
    'contact.form.success.title': 'Consulta Recibida',
    'contact.form.success.message': 'Gracias,',
    'contact.form.success.detail': '. Nos pondremos en contacto con usted en',
    'contact.form.success.hours': '24 horas hábiles',
    'contact.form.success.another': 'Enviar otra consulta',
    'contact.form.commodity.0': 'Productos del mar',
    'contact.form.commodity.1': 'Cordero patagónico',
    'contact.form.commodity.2': 'Aceite de oliva virgen extra',
    'contact.form.commodity.3': 'Quinoa, chía y sésamo',
    'contact.form.commodity.4': 'Café',
    'contact.form.commodity.5': 'Azúcares de caña',
    'contact.form.commodity.6': 'Importador de registro / Otro',

    'contact.sales.label': 'Líneas Directas',
    'contact.sales.title1': 'Ventas y',
    'contact.sales.titleItalic': 'Contactos de Soporte',
    'contact.sales.0.title': 'Miembro Gerente y Jefe de Ventas',
    'contact.sales.1.title': 'Consultas de Comercio Internacional',
    'contact.sales.2.title': 'Regulatorio y Documentación',
    'contact.sales.region.0': 'Américas',
    'contact.sales.whatsapp': 'WhatsApp · Argentina, en origen',
    'contact.sales.region.1': 'Mundial',
    'contact.sales.region.2': 'Todas las Jurisdicciones',

    'contact.offices.label': 'Dónde Estamos',
    'contact.offices.title1': 'Dónde',
    'contact.offices.titleItalic': 'Operamos',
    'contact.offices.0.label': 'Estados Unidos',
    'contact.offices.0.type': 'Domicilio legal',
    'contact.offices.0.hours': 'Lun–Vie, 08:00–18:00 MST',
    'contact.offices.1.label': 'Argentina, en origen',
    'contact.offices.1.type': 'Presencia propia en origen',
    'contact.offices.1.hours': 'Lun–Vie, 09:00–18:00 ART',
    'contact.offices.detail.address': 'Dirección',
    'contact.offices.detail.registration': 'Registro',
    'contact.offices.detail.timezone': 'Zona Horaria',
    'contact.offices.detail.hours': 'Horario Comercial',

    'contact.response.label': 'Nuestro Compromiso',
    'contact.response.title1': 'Tiempo de Respuesta',
    'contact.response.titleItalic': 'que Cumplimos',
    'contact.response.description': 'En el comercio de alimentos, el tiempo importa. Respondemos cada consulta dentro de un día hábil, sin excepciones, y las urgencias se atienden primero.',
    'contact.response.sla': 'Toda consulta recibe respuesta, aunque la respuesta sea que no podemos',
    'contact.response.0.timeframe': 'Prioridad',
    'contact.response.0.label': 'Urgencias comerciales',
    'contact.response.0.description': 'Las necesidades críticas de abastecimiento, las operaciones con fecha y las escaladas de cumplimiento pasan al frente de la fila. Atendemos urgencias: escríbanos indicándolo y se trata primero.',
    'contact.response.1.timeframe': '< 24 Horas',
    'contact.response.1.label': 'Consultas Comerciales Estándar',
    'contact.response.1.description': 'Todas las consultas generales, propuestas de asociación y solicitudes de abastecimiento de productos reciben una respuesta detallada en un día hábil.',
    'contact.response.2.timeframe': '< 48 Horas',
    'contact.response.2.label': 'Cumplimiento y Documentación',
    'contact.response.2.description': 'Las solicitudes de informes de cumplimiento, documentación de auditoría, verificación de certificaciones y orientación regulatoria se procesan en dos días hábiles.',

    // Compliance Page
    'compliance.hero.label': 'Confianza y Transparencia',
    'compliance.hero.title1': 'Construido sobre',
    'compliance.hero.title2': 'Estándares',
    'compliance.hero.titleItalic': 'Verificados',
    'compliance.hero.description': 'MERCALIA Connect Group LLC opera con permiso federal propio ante NOAA Fisheries y bajo los marcos regulatorios que rigen el ingreso de productos del mar a los Estados Unidos. Cada dato de esta sección es verificable de forma independiente.',
    'compliance.hero.cta.report': 'Solicitar Informe de Cumplimiento',
    'compliance.hero.cta.services': 'Nuestros Servicios',
    'compliance.hero.breadcrumb.home': 'Inicio',
    'compliance.hero.breadcrumb.compliance': 'Cumplimiento',
    'compliance.hero.stat.0.label': 'Permiso IFTP00104977 — vigente hasta julio de 2027',
    'compliance.hero.stat.1.label': 'N.º 149902101',
    'compliance.hero.stat.2.label': 'LLC registrada N.º 0008002716',
    'compliance.hero.stat.3.label': 'Marco FDA aplicable a productos del mar',

    'compliance.usperm.label': 'Permisos de Importación · Estados Unidos',
    'compliance.usperm.title1': 'Permiso propio para importar',
    'compliance.usperm.titleItalic': 'productos del mar a los Estados Unidos',
    'compliance.usperm.description': 'MERCALIA Connect Group LLC opera con permiso propio de importación de productos pesqueros ante NOAA Fisheries, y estructura sus operaciones bajo los marcos regulatorios federales que rigen el ingreso de productos del mar a los Estados Unidos.',
    'compliance.usperm.issuer': 'Emitido por',
    'compliance.usperm.status.active': 'Vigente',
    'compliance.usperm.status.framework': 'Marco aplicable',
    'compliance.usperm.status.online': 'En línea',
    'compliance.usperm.iftp.name': 'Permiso Internacional de Comercio Pesquero (IFTP)',
    'compliance.usperm.iftp.description': 'Permiso federal emitido a nombre de MERCALIA Connect Group LLC. Es el requisito para importar a los Estados Unidos las especies alcanzadas por el programa de monitoreo de productos del mar. Vigente hasta el 30 de julio de 2027.',
    'compliance.usperm.simp.name': 'Seafood Import Monitoring Program (SIMP)',
    'compliance.usperm.simp.description': 'Régimen de trazabilidad de NOAA aplicable al langostino y al pez espada: exige declarar la cadena de custodia desde la captura hasta el punto de ingreso. Las especies fuera del SIMP —merluza, calamar, vieira y pulpo— ingresan sin esa declaración.',
    'compliance.usperm.fda.name': 'HACCP de Productos Pesqueros — FDA',
    'compliance.usperm.fda.description': 'El ingreso de langostino a los Estados Unidos se rige por la Parte 123 del Título 21 del Código Federal de Regulaciones —control HACCP de pescados y mariscos— y no por el programa FSVP. Nuestras operaciones se estructuran bajo ese encuadre.',
    'compliance.usperm.verify.name': 'Verificador de Documentos',
    'compliance.usperm.verify.description': 'Cada carta, oferta y documento comercial que emitimos lleva un número de referencia propio, verificable en línea por cualquier contraparte, sin registro previo ni contraseña.',
    'compliance.usperm.cta': 'Verificar un documento',
    'compliance.usperm.note': 'La información regulatoria de esta sección describe el encuadre bajo el cual MERCALIA Connect Group LLC estructura sus operaciones de importación. No constituye asesoramiento regulatorio ni oferta contractual. Las habilitaciones sanitarias de planta corresponden en cada caso a los productores y procesadores de origen.',

    'compliance.certs.label': 'Certificaciones',
    'compliance.certs.title1': 'Reconocidas',
    'compliance.certs.titleItalic': 'Internacionalmente',
    'compliance.certs.description': 'Nuestras certificaciones son auditadas de forma independiente y renovadas anualmente, reflejando nuestro compromiso con la excelencia operativa de nuestros proveedores.',
    'compliance.certs.status.active': 'Activo',
    'compliance.certs.status.renewed': 'Renovado',
    'compliance.certs.issuedBy': 'Emitido por',
    'compliance.certs.producerNote': 'Las certificaciones SGS y Bureau Veritas (Veritas Qualitas) que figuran en este portal no son en todos los casos propias de MERCALIA Connect Group LLC. En su mayoría corresponden a nuestros productores y proveedores verificados, cuyas certificaciones respaldan la calidad e integridad de los productos que comercializamos.',

    'compliance.certs.0.name': 'Sistema de Gestión de Calidad',
    'compliance.certs.0.description': 'Procesos de gestión de calidad certificados que cubren adquisiciones, coordinación logística y prestación de servicios al cliente en todas las categorías de materias primas.',
    'compliance.certs.1.name': 'Gestión Ambiental',
    'compliance.certs.1.description': 'Gestión ambiental integrada en nuestras operaciones de cadena de suministro.',
    'compliance.certs.2.name': 'Salud y Seguridad Ocupacional',
    'compliance.certs.2.description': 'Estándares de seguridad laboral aplicados a todas las instalaciones operativas y sitios de socios.',
    'compliance.certs.3.name': 'Gestión de Salud y Seguridad',
    'compliance.certs.3.description': 'Gestión integral de salud y seguridad en todos los puntos operativos.',
    'compliance.certs.4.name': 'Seguridad de la Cadena de Suministro',
    'compliance.certs.4.description': 'Gestión de seguridad de la cadena de suministro de extremo a extremo para envíos de materias primas de alto valor, garantizando la integridad desde el origen hasta el destino.',

    'compliance.reg.label': 'Cumplimiento Regulatorio',
    'compliance.reg.title1': 'Marcos en los que',
    'compliance.reg.titleItalic': 'Operamos',
    'compliance.reg.description': 'Son los marcos contra los que controlamos cada operación: sanciones (OFAC), prevención de lavado (AML), conocimiento del cliente (KYC) y normas anticorrupción.',

    'compliance.quality.label': 'Estándares de Calidad',
    'compliance.quality.title1': 'Precisión en',
    'compliance.quality.titleItalic': 'Cada Etapa',
    'compliance.quality.description': 'Nuestro marco de gestión de calidad no es una casilla de verificación — es una disciplina operativa integrada en cada flujo de trabajo.',
    'compliance.quality.annualAudit.title': 'Auditorías de Calidad Anuales por Terceros',
    'compliance.quality.annualAudit.description': 'Todos los estándares de calidad son verificados de forma independiente por organismos de auditoría acreditados anualmente, con revisiones intermedias realizadas trimestralmente.',
    'compliance.quality.0.title': 'Protocolo de Verificación de Proveedores',
    'compliance.quality.0.description': 'Cada proveedor pasa por una evaluación de debida diligencia de 47 puntos que cubre estabilidad financiera, capacidad operativa, situación regulatoria y prácticas éticas antes de la incorporación.',
    'compliance.quality.0.m0': 'Criterios de Verificación',
    'compliance.quality.0.m1': 'Tiempo Promedio de Revisión',
    'compliance.quality.1.title': 'Aseguramiento de Calidad del Producto',
    'compliance.quality.1.description': 'Inspecciones independientes de terceros en origen, tránsito y destino. Todas las materias primas se prueban contra benchmarks de calidad reconocidos internacionalmente antes de la aceptación.',
    'compliance.quality.1.m0': 'Puntos de Inspección',
    'compliance.quality.1.m1': 'Tasa de Rechazo',
    'compliance.quality.2.title': 'Integridad de la Documentación',
    'compliance.quality.2.description': 'Toda la documentación comercial — certificados de origen, certificados fitosanitarios, conocimientos de embarque y declaraciones aduaneras — se verifica para autenticidad y completitud.',
    'compliance.quality.2.m0': 'Tipos de Documentos',
    'compliance.quality.2.m1': 'Tasa de Verificación',
    'compliance.quality.3.title': 'Monitoreo Continuo',
    'compliance.quality.3.description': 'Monitoreo de cumplimiento en tiempo real en todas las transacciones activas. Las alertas automatizadas señalan desviaciones de los umbrales regulatorios, permitiendo acciones correctivas inmediatas.',
    'compliance.quality.3.m0': 'Frecuencia de Monitoreo',
    'compliance.quality.3.m1': 'Respuesta a Alertas',

    'compliance.audit.label': 'Documentación de Auditoría',
    'compliance.audit.title1': 'Registro de',
    'compliance.audit.titleItalic': 'Auditoría Transparente',
    'compliance.audit.note': 'Todos los informes de auditoría están disponibles para socios y clientes verificados previa solicitud.',
    'compliance.audit.requestReport': 'Solicitar Informe Completo',
    'compliance.audit.status.passed': 'Aprobado',
    'compliance.audit.status.inReview': 'En Revisión',
    'compliance.audit.status.scheduled': 'Programado',
    'compliance.audit.scope': 'Alcance',
    'compliance.audit.findings': 'Hallazgos Clave',
    'compliance.audit.summary.0.label': 'Auditorías Completadas',
    'compliance.audit.summary.0.sub': 'Este ciclo',
    'compliance.audit.summary.1.label': 'Tasa de Aprobación',
    'compliance.audit.summary.1.sub': 'Todas las auditorías completadas',
    'compliance.audit.summary.2.label': 'Próxima Programada',
    'compliance.audit.summary.2.sub': 'Revisión GDPR',

    'compliance.audit.0.title': 'Auditoría Anual de Gestión de Calidad ISO 9001',
    'compliance.audit.0.findings': 'Cero no conformidades. 3 oportunidades de mejora identificadas y ejecutadas.',
    'compliance.audit.0.scope': 'Todos los procesos operativos, gestión de proveedores, prestación de servicios al cliente',
    'compliance.audit.1.title': 'Revisión de Cumplimiento AML y KYC',
    'compliance.audit.1.findings': 'Cumplimiento total confirmado. Procedimientos de verificación mejorados implementados.',
    'compliance.audit.1.scope': 'Incorporación de clientes, monitoreo de transacciones, verificación de sanciones',
    'compliance.audit.2.title': 'Evaluación de Seguridad de la Cadena de Suministro',
    'compliance.audit.2.findings': 'Requisitos ISO 28000 completamente cumplidos. Sistemas de seguimiento digital elogiados.',
    'compliance.audit.2.scope': 'Cadena de suministro de extremo a extremo, socios logísticos, procedimientos aduaneros',
    'compliance.audit.3.title': 'Auditoría de Cumplimiento Ambiental',
    'compliance.audit.3.findings': 'Auditoría en progreso. Hallazgos preliminares indican cumplimiento total.',
    'compliance.audit.3.scope': 'Sistema de gestión ambiental, huella de carbono, gestión de residuos',
    'compliance.audit.4.title': 'Auditoría de Protección de Datos y GDPR',
    'compliance.audit.4.findings': 'Programada para junio 2025. Autoevaluación previa a la auditoría completada.',
    'compliance.audit.4.scope': 'Actividades de procesamiento de datos, gestión de consentimiento, derechos de los interesados',

    'compliance.cta.label': 'Asociarse con Confianza',
    'compliance.cta.title1': 'El Cumplimiento No Es',
    'compliance.cta.titleItalic': 'Opcional Aquí',
    'compliance.cta.description': 'Antes de operar verificamos a nuestra contraparte, y usted puede verificarnos a nosotros: registro de la sociedad, D-U-N-S, permiso NOAA y declaración BOI. Pídanos la hoja de información corporativa para su debida diligencia.',
    'compliance.cta.cta.dossier': 'Solicitar documentación corporativa',
    'compliance.cta.cta.services': 'Explorar Servicios',
    'compliance.cta.trust.0': 'Permiso NOAA vigente',
    'compliance.cta.trust.1': 'Documentos verificables',
    'compliance.cta.trust.2': 'Controles OFAC · AML · KYC',
  },

  en: {
    // Header / Nav
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.products': 'Products',
    'nav.compliance': 'Compliance',
    'nav.contact': 'Contact',
    'nav.contactUs': 'Contact Us',

    // Footer
    'footer.rights': '© 2026 MERCALIA Connect Group LLC. All rights reserved.',
    'footer.tagline': 'Your strategic partner at origin · New Mexico, USA',
    'footer.verify': 'Verify credentials',
    'footer.ca': 'Verified by ConnectAmericas',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',

    // Hero Section
    'hero.label': 'Argentina · Brazil · Bolivia · Chile · Paraguay · Peru · Central America',
    'hero.line1': 'From the plant of origin',
    'hero.line2': 'to your warehouse.',
    'hero.line3': '',
    'hero.line4': '',
    'hero.description': 'Our specialty: wild-caught Argentine red shrimp (Pleoticus muelleri) and Patagonian seafood, from export-approved plants. We also supply, by the container and from origin, Patagonian lamb, cane sugars, extra virgin olive oil, quinoa and coffee. See the full list under Products.',
    'hero.pronto': '',
    'hero.cta.services': 'View Products',
    'hero.cta.products': 'Verify credentials',
    'hero.scroll': 'Scroll',
    'hero.badge.iso': 'NOAA permit IFTP00104977 · valid to 2027',
    'hero.badge.ofac': 'D-U-N-S® 149902101',
    'hero.badge.aml': 'Direct from the plant',
    'hero.badge.llc': 'Verified by ConnectAmericas',

    // Stats Section
    'stats.label': 'By the Numbers',
    'stats.title': 'What stands behind every offer',
    'home.permit.title': 'Our own federal permit to import seafood into the United States',
    'home.permit.subtitle': 'International Fisheries Trade Permit — NOAA Fisheries · No.',
    'home.permit.cta': 'See permits & compliance',

    'stats.0.label': 'Origins',
    'stats.0.description': 'Argentina, Brazil, Bolivia, Chile, Paraguay, Peru and Central America, depending on the product and the fishing or growing area',
    'stats.1.label': 'First-hand product lines',
    'stats.1.description': 'Seafood · Patagonian lamb · extra virgin olive oil · quinoa, chia and sesame · coffee · cane sugars',
    'stats.2.label': 'Direct from the producer',
    'stats.2.description': 'We buy first-hand at the processing plant, the packing house or the mill of origin. No hidden layers, no third-party resale — and we stand behind that',
    'stats.2.display': 'DIRECT',

    // Services Overview
    'servicesOverview.label': 'What We Do',
    'servicesOverview.title1': 'First-Hand Food Trade,',
    'servicesOverview.title2': 'Solutions',
    'servicesOverview.titleItalic': 'End to End',
    'servicesOverview.allServices': 'All Services',
    'servicesOverview.learnMore': 'Learn more',
    'servicesOverview.0.title': 'Sourcing at Origin',
    'servicesOverview.0.description': 'We buy first-hand at the fishing plant, the packing house, the mill or the winery, and sell to you in our own name: we take title to the goods and hold the bill of lading. No reseller in between.',
    'servicesOverview.1.title': 'Origin Pricing & Seasonal Windows',
    'servicesOverview.1.description': 'We track price, season and availability at the plants we work with, and tell you when to buy and when to wait, before we quote.',
    'servicesOverview.2.title': 'Logistics & Operations',
    'servicesOverview.2.description': 'Refrigerated or dry containers, full or consolidated, from South and Central American ports; documentation and delivery at the destination port or, in the United States, at your warehouse.',
    'servicesOverview.3.title': 'Compliance & Risk',
    'servicesOverview.3.description': 'We screen every counterparty against the international sanctions lists and run anti-money-laundering and know-your-customer checks (OFAC, AML and KYC) before each transaction.',
    'servicesOverview.4.title': 'Payment & Documents',
    'servicesOverview.4.description': 'We settle the Incoterm, the payment instrument and who controls the bill of lading before the cargo moves. Mercalia keeps documentary control until payment is complete.',
    'servicesOverview.5.title': 'Service for Exporters',
    'servicesOverview.5.badge': 'Importer of Record · U.S.',
    'servicesOverview.5.description': 'Do you export food and want to sell it in the United States? We offer an importer of record service for exporters, with a licensed broker and a presence in the country. First step: a free assessment.',

    // Commodities Showcase
    'commodities.label': 'Product Portfolio',
    'commodities.title1': 'Our Main',
    'commodities.title2': 'Product',
    'commodities.titleItalic': 'Lines',
    'commodities.primaryLabel': 'Specialty: Wild-Caught Argentine Red Shrimp · Also: Patagonian Lamb · Cane Sugars · Olive Oil · Quinoa · Coffee',
    'commodities.fullPortfolio': 'Full Portfolio',

    // Why Mercalia
    'why.label': 'Why Choose Us',
    'why.title1': 'Your Strategic Partner',
    'why.title2': 'at Origin',
    'why.titleItalic': 'at Origin',
    'why.title3': 'for South & Central American Food',
    'why.description': 'In a market with counterparty risk and strict regulatory controls, MERCALIA Connect Group LLC works by clear rules: we screen every counterparty, document every step and tell you the truth about price, season and availability.',
    'why.stats.volume': 'Our own federal permit',
    'why.stats.since': 'NOAA Fisheries · No. IFTP00104977',
    'why.stats.growth': 'Valid through July 2027',
    'why.badge': 'Active NOAA Federal Permit',
    'why.0.title': 'Full Compliance Architecture',
    'why.0.description': 'Before every transaction we screen the counterparty against the international sanctions lists and run anti-money-laundering and know-your-customer checks (OFAC, AML and KYC). That protects our clients from legal and reputational risk. We verify every new client before onboarding: we request their corporate information sheet (CIS) and proof of funds (POF).',
    'why.1.title': 'Verified Plants at Origin',
    'why.1.description': 'We deal directly with export-approved plants in South and Central America. No reseller in between: we know exactly where your goods come from and how they move.',
    'why.2.title': 'Origin pricing and seasonal windows',
    'why.2.description': 'We track price and availability at the plants we work with, and tell you when to buy and when to wait. If the fishing window or the season does not support it, you hear that before we quote.',
    'why.3.title': 'One point of contact, start to finish',
    'why.3.description': 'The same person handles your business from first inquiry to delivery. No handovers, no ticket desk, no explaining your requirement twice.',
    'why.4.title': 'Season continuity',
    'why.4.description': 'We work with plants that freeze both at sea and onshore, which determines what presentation is available in each month of the year. We plan your supply around that real calendar, not around a promise.',

    // Home CTA
    'homeCTA.badge': 'Available for New Partnerships',
    'homeCTA.title1': 'Ready to Source',
    'homeCTA.titleItalic': 'Globally?',
    'homeCTA.description': 'Whether you need a single bulk shipment or a long-term supply agreement, we are ready to structure the right solution for your business.',
    'homeCTA.cta.start': 'Start a Conversation',
    'homeCTA.cta.services': 'Explore Services',
    'homeCTA.trust.0': 'Confidential Inquiries',
    'homeCTA.trust.1': 'No Commitment Required',
    'homeCTA.trust.2': 'Response within 24h',

    // Services Page
    'services.hero.label': 'What We Offer',
    'services.hero.title1': 'Global Solutions',
    'services.hero.title2': 'in International',
    'services.hero.titleItalic': 'Trade',
    'services.hero.description': 'We buy first-hand in South and Central America and deliver where the buyer needs it, from the first inquiry to final delivery.',
    'services.hero.cta.consult': 'Request a Quote',
    'services.hero.cta.products': 'View Products',
    'services.hero.breadcrumb.home': 'Home',
    'services.hero.breadcrumb.services': 'Services',

    'services.grid.label': 'Core Services',
    'services.grid.title1': 'Sourcing at Origin',
    'services.grid.titleItalic': '& Trade Services',

    'services.0.title': 'International Sourcing & Procurement',
    'services.0.description': 'We buy directly from export-approved fishing plants, meat processors, sugar mills and grain elevators in South and Central America, and sell to you in our own name. Containers ship from the plant at origin directly to you, with no reseller in between.',
    'services.0.f0': 'Supplier verification & due diligence',
    'services.0.f1': 'Origin certification',
    'services.0.f2': 'One point of contact end to end',
    'services.0.f3': 'FOB and CFR quoted side by side',

    'services.1.title': 'Seafood & Cold Chain',
    'services.1.description': 'Argentine wild red shrimp and other South Atlantic and Pacific species. We handle the season window, the difference between frozen at sea and land-frozen, and the entry documentation for the United States.',
    'services.1.f0': 'NOAA IFTP permit & SIMP declaration',
    'services.1.f1': 'Frozen at sea or land-frozen',
    'services.1.f2': '40-foot refrigerated container',

    'services.2.title': 'Payment Structure & Documentation',
    'services.2.description': 'We settle the payment structure before the cargo moves: INCOTERM, instrument, and who controls the bill of lading. Mercalia keeps documentary control until payment is complete.',
    'services.2.f0': 'We quote FOB, FCA, CFR and CIF',
    'services.2.f1': 'Payment against documents',
    'services.2.f2': 'Complete export document set',
    'services.2.f3': 'DAP and DDP delivery within the United States',

    'services.3.title': 'Quinoa, Chia, Sesame, Olive Oil & Sugar',
    'services.3.description': 'Quinoa, chia and sesame; extra virgin olive oil; and cane sugars: refined white, raw and demerara, light brown and muscovado. Origins in South and Central America, by the container depending on volume and destination port.',
    'services.3.f0': 'Quinoa, chia & sesame',
    'services.3.f1': 'Extra virgin olive oil',
    'services.3.f2': 'Sugars from Brazil, Argentina and Central America',

    'services.4.title': 'Coffee',
    'services.4.description': 'Green and roasted coffee from plantations in El Salvador and Brazil, traceable to the grower.',
    'services.4.f0': 'Traceable to the grower',
    'services.4.f1': 'Green and roasted coffee from El Salvador and Brazil',
    'services.4.f2': 'Various presentations & packing',

    'services.5.title': 'Patagonian Lamb',
    'services.5.description': 'Grass-fed Patagonian lamb from plants approved by SENASA for sheep meat export: the season runs October to January, peaking in November and December, and is committed in advance. Lamb destinations include Central America, the Caribbean, Europe, the Middle East and Israel.',
    'services.5.f0': 'Lamb: season October to January, peak November–December',
    'services.5.f1': 'Halal and Kosher subject to plant and destination',
    'services.5.f2': 'Carcasses, bone-in cuts and boneless cuts',
    'services.5.f3': 'Chilled or frozen, by destination and transit time',

    'services.howWeWork.label': 'Our Process',
    'services.howWeWork.title1': 'How We Execute',
    'services.howWeWork.titleItalic': 'Every Transaction',
    'services.howWeWork.description': 'A structured, accountable process from first contact to final delivery — designed to minimize risks and optimize outcomes in international commodity trade.',
    'services.step.0.title': 'Initial Inquiry',
    'services.step.0.description': 'You submit your commodity requirements — type, volume, origin preferences, delivery timeline, and budget parameters.',
    'services.step.1.title': 'Needs Assessment',
    'services.step.1.description': 'We review your requirements and, if needed, set up a call to clarify specifications, season and market conditions.',
    'services.step.2.title': 'Compliance Screening',
    'services.step.2.description': 'Before any commercial engagement we screen all parties against the international sanctions lists and run anti-money-laundering and know-your-customer checks (OFAC, AML and KYC).',
    'services.step.3.title': 'Sourcing & Quoting',
    'services.step.3.description': 'We check price and availability at the plants we work with, confirm the product specifications and send you a formal written offer.',
    'services.step.4.title': 'Contract Execution',
    'services.step.4.description': 'We sign the contract with you (Incoterm, payment instrument and inspection) and close the purchase at origin in Mercalia’s own name.',
    'services.step.5.title': 'Delivery & Settlement',
    'services.step.5.description': 'Full logistics coordination, documentation management, and payment settlement. We remain accountable until the commodity is delivered and all obligations are fulfilled.',

    'services.cta.title': 'Ready to Connect Markets?',
    'services.cta.description': 'Our team is available to discuss your commodity sourcing needs, review your requirements, and propose the right solution — confidentially and without obligation. Reach us at',
    'services.cta.whatsapp': 'WhatsApp — Argentina, at origin',
    'services.cta.inquiry': 'Send an Inquiry',
    'services.cta.trust.0': 'Confidential Inquiries',
    'services.cta.trust.1': 'No Commitment Required',
    'services.cta.trust.2': 'South & Central American Origin',

    // Products Page
    'products.hero.label': 'Product Portfolio',
    'products.hero.title1': 'First-Hand',
    'products.hero.titleItalic': 'Portfolio',
    'products.hero.description': 'Full product list from Argentina, Brazil, Bolivia, Chile, Paraguay, Peru and Central America. Our specialty is wild-caught Argentine red shrimp; everything else we supply by the container and from origin. Containers ship from the plant at origin directly to the buyer, with no reseller in between.',
    'products.hero.cta': 'Request Product Inquiry',
    'products.hero.breadcrumb.home': 'Home',
    'products.hero.breadcrumb.products': 'Products',
    'products.hero.stat.0.label': 'First-Hand Lines',
    'products.hero.stat.0.sub': 'From producer to buyer',
    'products.hero.stat.1.label': 'Origins',
    'products.hero.stat.1.sub': 'South and Central America',
    'products.hero.stat.2.label': 'Delivery Format',
    'products.hero.stat.2.sub': 'Dry and refrigerated container',
    'products.hero.stat.3.label': 'Response Time',
    'products.hero.stat.3.sub': 'For standard inquiries',

    'products.grid.label': 'Product Categories',
    'products.grid.title1': 'What We Source',
    'products.grid.titleItalic': '& Trade',
    'products.grid.inquire': 'Inquire about this category',
    'products.grid.note': "Looking for something that isn't on this list?",
    'products.grid.contact': 'Contact us',
    'products.grid.noteLink': "and we'll tell you straight whether we can supply it.",
    'products.grid.filter.all': 'All',

    'products.cat.0.name': 'Seafood',
    'products.cat.0.description': 'Argentine wild red shrimp head-on, tails and peeled; hake and Patagonian toothfish, Illex squid, Patagonian scallop, octopus, swordfish and albacore, king crab, mussels, salmon, trout and premium caviar. Frozen at sea or land-frozen depending on species and season.',
    'products.cat.1.name': 'Patagonian Lamb',
    'products.cat.1.description': 'Grass-fed, from export-approved plants. Season October to January, peak November–December. Destinations include Central America, the Caribbean, Europe, the Middle East and Israel.',
    'products.cat.3.name': 'Coffee',
    'products.cat.3.description': 'Green and roasted coffee from plantations in El Salvador and Brazil, traceable to the grower.',
    'products.cat.4.name': 'Cane Sugars',
    'products.cat.4.description': 'From mills in Brazil, Argentina and Central America. Dry container, big bags or bulk vessel; private label on order.',
    'products.cat.6.name': 'Quinoa, Chia & Sesame',
    'products.cat.6.description': 'Conventional white quinoa from Bolivia, Oruro Altiplano, lot tested by an ISO/IEC 17025 accredited lab: gluten not detected. Black or white chia from Paraguay, organic or conventional. Natural white sesame seeds, unhulled, Sortex cleaned. By container, with volume programs.',

    'products.tag.0': 'Seafood',
    'products.tag.1': 'Lamb',
    'products.tag.3': 'Coffee',
    'products.tag.4': 'Sugar',
    'products.tag.6': 'Quinoa, Chia & Sesame',
    'products.cat.7.name': 'Extra Virgin Olive Oil',
    'products.cat.7.description': 'Extra virgin olive oil in bulk, from a plant with certified HACCP, and bottled under the estate\'s brand or the buyer\'s private label.',
    'products.tag.7': 'Olive Oil',
    'products.grid.credits': 'Illustrative images.',

    'products.sourcing.label': 'Origins & Loading Ports',
    'products.sourcing.title1': 'Where the Cargo',
    'products.sourcing.titleItalic': 'Ships From',
    'products.sourcing.delivery.label': 'Shipping Formats',
    'products.sourcing.deliveryReach': 'Loading Ports',
    'products.sourcing.deliveryTitle': 'South Atlantic & Pacific',
    // Global reach + Importer of record (Compliance)
    'reach.label': 'Commercial Reach',
    'reach.title': 'The origin is South and Central American. The destination is the buyer’s choice.',
    'reach.lead': 'Mercalia works with buyers in any market. We ship from ports in South and Central America to the following destination markets.',
    'reach.market.0': 'European Union and United Kingdom',
    'reach.market.1': 'United Arab Emirates and the Gulf',
    'reach.market.2': 'Asia',
    'reach.market.3': 'Africa',
    'reach.market.4': 'North America',
    'reach.market.5': 'Rest of the Americas',
    'reach.quoting': 'We quote FOB, FCA, CFR and CIF to any destination, in full or consolidated containers, with export documentation and control of the bill of lading held by Mercalia.',
    'reach.caveat': 'Health approvals and market access requirements depend on the product and the destination market, and are confirmed by establishment before any firm quotation.',

    'ior.label': 'And for the United States',
    'ior.title': 'Something most exporters at origin do not offer',
    'ior.p1': 'Mercalia Connect Group LLC is a company incorporated in the United States, based in New Mexico and holding its own D-U-N-S® number. Besides sourcing at origin, we offer an importer of record service for exporters, with a licensed broker and a presence in the country.',
    'ior.p2': 'In practice, a U.S. buyer can source directly at origin and receive the goods at its warehouse.',
    'ior.dap.badge': 'Recommended',
    'ior.dap.title': 'Delivered at the agreed place',
    'ior.dap.body': 'Mercalia handles import clearance for the buyer’s account. Duties, fees and surcharges in force at the date of entry are re-invoiced at cost, against supporting documentation and with no mark-up.',
    'ior.ddp.title': 'Delivered with duties and taxes included',
    'ior.ddp.body': 'The price includes import duties and taxes. Subject to confirmation on a per-shipment basis.',
    'ior.recommendation.a': 'We recommend DAP. Under DDP, duties sit inside a closed price, and that price must account for tariff movement between quotation and date of entry. Under DAP the buyer',
    'ior.recommendation.b': 'pays exactly what Customs assessed',
    'ior.recommendation.c': 'and sees the documentation, without paying a premium for a risk no one controls.',

    // Service for exporters: importer of record (master text approved by José, 9-Oct-2026)
    'xsvc.label': 'Service for exporters',
    'xsvc.title': 'Importer of Record in the United States',
    'xsvc.meta': 'Mercalia Connect Group LLC · a U.S. company · New Mexico, USA',
    'xsvc.banner.lead': 'Do you export food and want to sell in the United States? We offer an importer of record service for exporters, with a licensed broker and a presence in the country.',
    'xsvc.banner.cta': 'See the service',
    'xsvc.why.title': 'Why you need it',
    'xsvc.why.body': 'Selling food in the United States takes more than a good product and a buyer. Someone has to stand before U.S. Customs (CBP) and the FDA as the importer of the goods, with a customs bond, a licensed broker and a presence in the country. Most exporting companies do not have that structure. Mercalia Connect Group LLC, a U.S. company, offers that service to exporters.',
    'xsvc.resp.title': 'What the importer of record is responsible for',
    'xsvc.resp.body': 'The importer of record answers to the U.S. government for the entire import: that the goods are correctly declared and classified, that duties are paid and that entry requirements are met. If something goes wrong, fines, notices, holds or redelivery orders fall on the importer. That is why a customs bond is required to guarantee these obligations to the government. This is the responsibility U.S. law places on the importer of record.',
    'xsvc.do.title': 'What we do for you',
    'xsvc.do.1.title': 'Your product and your plant, in order',
    'xsvc.do.1.body': 'We review and organize everything the U.S. requires before loading: FDA facility registration, company identification (DUNS number), labeling, origin, product documentation and any specific permits for your category.',
    'xsvc.do.2.title': 'Entry and delivery',
    'xsvc.do.2.body': 'We act as importer of record, coordinate clearance with a licensed customs broker, file the FDA prior notice, handle customs requirements and follow the goods until they reach your customer in the United States.',
    'xsvc.how.title': 'How we work',
    'xsvc.how.body': 'Every shipment follows a procedure with checkpoints at each stage: if a requirement is not met, we do not move forward. We would rather solve it at origin than face a problem at the port.',
    'xsvc.diag.title': 'First step: a free assessment',
    'xsvc.diag.body': 'We tell you whether your product and documents are ready to enter the United States, and what is missing if they are not.',
    'xsvc.diag.cta': 'Request your assessment',
    'xsvc.diag.subject': 'Free assessment — importer of record in the U.S.',
    'xsvc.downloads': 'Downloads',
    'xsvc.dl.sheet': 'Service sheet (PDF, English)',
    'xsvc.dl.sheetAlt': 'Hoja del servicio (PDF, español)',
    'xsvc.dl.catalog': 'Product catalog Rev. 7 (PDF, English-Spanish)',
    'xsvc.note': 'Commercial information. Service terms are set in writing in each contract.',

    'products.sourcing.incoterms': 'INCOTERMS 2020 We Quote',
    'products.sourcing.incoterms.note': 'FOB, FCA, CFR and CIF to any destination. DAP and DDP apply to deliveries within the United States, where Mercalia is in a position to act as importer of record. DDP subject to confirmation on a per-shipment basis.',
    'products.sourcing.region.0': 'Argentina',
    'products.sourcing.region.0.desc': 'Seafood, Patagonian lamb, sugar and olive oil',
    'products.sourcing.region.0.ports': 'Loading port confirmed per shipment',
    'products.sourcing.region.1': 'Brazil',
    'products.sourcing.region.1.desc': 'Sugar and coffee',
    'products.sourcing.region.1.ports': 'Loading port confirmed per shipment',
    'products.sourcing.region.2': 'Chile',
    'products.sourcing.region.2.desc': 'Pacific seafood, depending on species and season',
    'products.sourcing.region.2.ports': 'Loading port confirmed per shipment',
    'products.sourcing.region.3': 'Peru',
    'products.sourcing.region.3.desc': 'Pacific seafood, depending on species and season',
    'products.sourcing.region.3.ports': 'Loading port confirmed per shipment',
    'products.sourcing.region.4': 'Bolivia',
    'products.sourcing.region.4.desc': 'White quinoa from the Oruro Altiplano',
    'products.sourcing.region.4.ports': 'Arica (Chile)',
    'products.sourcing.region.5': 'Central America',
    'products.sourcing.region.5.desc': 'Cane sugar, and coffee from El Salvador',
    'products.sourcing.region.5.ports': 'Loading port confirmed per shipment',
    'products.sourcing.region.6': 'Paraguay',
    'products.sourcing.region.6.desc': 'Black and white chia, organic and conventional',
    'products.sourcing.region.6.ports': 'Asunción',
    'products.sourcing.delivery.0': 'Refrigerated Container',
    'products.sourcing.delivery.0.desc': '40 ft, frozen',
    'products.sourcing.delivery.1': 'Dry Container',
    'products.sourcing.delivery.1.desc': '20 and 40 ft, FCL',
    'products.sourcing.delivery.2': 'Consolidated',
    'products.sourcing.delivery.2.desc': 'LCL, small orders',
    'products.sourcing.delivery.3': 'Bulk',
    'products.sourcing.delivery.3.desc': 'On request, by volume',

    'products.cta.badge': 'Ready to Source',
    'products.cta.title1': 'Need a Specific',
    'products.cta.titleItalic': 'Product?',
    'products.cta.description': "Submit your specification — product, presentation, volume, origin, and delivery terms — and we'll come back with the sourcing options we have.",
    'products.cta.item.0.label': 'Product Inquiry',
    'products.cta.item.0.desc': 'Product, volume & specifications',
    'products.cta.item.1.label': 'Technical Data Sheet',
    'products.cta.item.1.desc': 'Species, presentations and origin',
    'products.cta.item.2.label': 'Season Agreement',
    'products.cta.item.2.desc': 'Committed volume for the next season',

    // Contact Page
    'contact.hero.label': 'Get In Touch',
    'contact.hero.title1': "Let's Start a",
    'contact.hero.titleItalic': 'Conversation',
    'contact.hero.description': 'Whether you are sourcing food at origin, need a quote delivered to your warehouse, or want to use our importer of record service, we reply within 24 hours.',
    'contact.hero.breadcrumb.home': 'Home',
    'contact.hero.breadcrumb.contact': 'Contact',
    'contact.hero.stat.0.label': 'Response time',
    'contact.hero.stat.1.label': 'Locations',
    'contact.hero.stat.2.label': 'Languages we work in',
    'contact.hero.stat.3.label': 'Our own NOAA permit',

    'contact.form.title': 'Send an Inquiry',
    'contact.form.name': 'Full Name',
    'contact.form.namePlaceholder': 'Name',
    'contact.form.company': 'Company',
    'contact.form.commodity': 'Product of Interest',
    'contact.form.commodityPlaceholder': 'Select a product line…',
    'contact.form.message': 'Message',
    'contact.form.messagePlaceholder': 'Describe your sourcing needs, volumes, target markets, or any specific requirements…',
    'contact.form.privacy': 'By submitting this form you agree to our privacy policy. Your information is used only to answer your inquiry and is never shared with third parties.',
    'contact.form.submit': 'Send Inquiry',
    'contact.form.success.title': 'Inquiry Received',
    'contact.form.success.message': 'Thank you,',
    'contact.form.success.detail': '. We will get back to you within',
    'contact.form.success.hours': '24 business hours',
    'contact.form.success.another': 'Submit another inquiry',
    'contact.form.commodity.0': 'Seafood',
    'contact.form.commodity.1': 'Patagonian Lamb',
    'contact.form.commodity.2': 'Extra Virgin Olive Oil',
    'contact.form.commodity.3': 'Quinoa, Chia & Sesame',
    'contact.form.commodity.4': 'Coffee',
    'contact.form.commodity.5': 'Cane Sugars',
    'contact.form.commodity.6': 'Importer of Record / Other',

    'contact.sales.label': 'Direct Lines',
    'contact.sales.title1': 'Sales &',
    'contact.sales.titleItalic': 'Support Contacts',
    'contact.sales.0.title': 'Managing Member & Head of Sales',
    'contact.sales.1.title': 'International Trade Inquiries',
    'contact.sales.2.title': 'Regulatory & Documentation',
    'contact.sales.region.0': 'Americas',
    'contact.sales.whatsapp': 'WhatsApp · Argentina, at origin',
    'contact.sales.region.1': 'Worldwide',
    'contact.sales.region.2': 'All Jurisdictions',

    'contact.offices.label': 'Where We Are',
    'contact.offices.title1': 'Where We',
    'contact.offices.titleItalic': 'Operate',
    'contact.offices.0.label': 'United States',
    'contact.offices.0.type': 'Registered office',
    'contact.offices.0.hours': 'Mon–Fri, 08:00–18:00 MST',
    'contact.offices.1.label': 'Argentina, at origin',
    'contact.offices.1.type': 'Own presence at origin',
    'contact.offices.1.hours': 'Mon–Fri, 09:00–18:00 ART',
    'contact.offices.detail.address': 'Address',
    'contact.offices.detail.registration': 'Registration',
    'contact.offices.detail.timezone': 'Timezone',
    'contact.offices.detail.hours': 'Business Hours',

    'contact.response.label': 'Our Commitment',
    'contact.response.title1': 'Response Time',
    'contact.response.titleItalic': 'We Keep',
    'contact.response.description': 'In food trade, timing matters. We answer every inquiry within one business day, no exceptions, and urgent matters are handled first.',
    'contact.response.sla': 'Every inquiry gets an answer, even when the answer is no',
    'contact.response.0.timeframe': 'Priority',
    'contact.response.0.label': 'Urgent trade inquiries',
    'contact.response.0.description': 'Critical sourcing needs, time-sensitive deals and compliance escalations go to the front of the queue. We do handle urgent matters: flag it in your message and it is taken first.',
    'contact.response.1.timeframe': '< 24 Hours',
    'contact.response.1.label': 'Standard Business Inquiries',
    'contact.response.1.description': 'All general inquiries, partnership proposals, and product sourcing requests receive a detailed response within one business day.',
    'contact.response.2.timeframe': '< 48 Hours',
    'contact.response.2.label': 'Compliance & Documentation',
    'contact.response.2.description': 'Requests for compliance reports, audit documentation, certification verification, and regulatory guidance are processed within two business days.',

    // Compliance Page
    'compliance.hero.label': 'Trust & Transparency',
    'compliance.hero.title1': 'Built on',
    'compliance.hero.title2': 'Verified',
    'compliance.hero.titleItalic': 'Standards',
    'compliance.hero.description': 'MERCALIA Connect Group LLC holds its own federal permit with NOAA Fisheries and operates under the regulatory frameworks governing seafood entry into the United States. Every detail in this section can be independently verified.',
    'compliance.hero.cta.report': 'Request Compliance Report',
    'compliance.hero.cta.services': 'Our Services',
    'compliance.hero.breadcrumb.home': 'Home',
    'compliance.hero.breadcrumb.compliance': 'Compliance',
    'compliance.hero.stat.0.label': 'Permit IFTP00104977 — valid through July 2027',
    'compliance.hero.stat.1.label': 'No. 149902101',
    'compliance.hero.stat.2.label': 'Registered LLC No. 0008002716',
    'compliance.hero.stat.3.label': 'Applicable FDA seafood framework',

    'compliance.usperm.label': 'U.S. Import Permits',
    'compliance.usperm.title1': 'Our own permit to import',
    'compliance.usperm.titleItalic': 'seafood into the United States',
    'compliance.usperm.description': 'MERCALIA Connect Group LLC holds its own federal fisheries trade permit with NOAA Fisheries, and structures its operations under the federal frameworks that govern seafood entering the United States.',
    'compliance.usperm.issuer': 'Issued by',
    'compliance.usperm.status.active': 'Active',
    'compliance.usperm.status.framework': 'Applicable framework',
    'compliance.usperm.status.online': 'Live',
    'compliance.usperm.iftp.name': 'International Fisheries Trade Permit (IFTP)',
    'compliance.usperm.iftp.description': 'Federal permit issued in the name of MERCALIA Connect Group LLC. It is the requirement to import into the United States the species covered by the seafood monitoring program. Valid through 30 July 2027.',
    'compliance.usperm.simp.name': 'Seafood Import Monitoring Program (SIMP)',
    'compliance.usperm.simp.description': 'NOAA traceability regime covering shrimp and swordfish: it requires declaring chain of custody from harvest to point of entry. Species outside SIMP — hake, squid, scallop and octopus — enter without that declaration.',
    'compliance.usperm.fda.name': 'Seafood HACCP — FDA',
    'compliance.usperm.fda.description': 'Shrimp entering the United States is governed by Title 21 of the Code of Federal Regulations, Part 123 — seafood HACCP control — and not by the FSVP program. Our operations are structured under that framework.',
    'compliance.usperm.verify.name': 'Document Verifier',
    'compliance.usperm.verify.description': 'Every letter, offer and commercial document we issue carries its own reference number, verifiable online by any counterparty, with no sign-up and no password.',
    'compliance.usperm.cta': 'Verify a document',
    'compliance.usperm.note': 'The regulatory information in this section describes the framework under which MERCALIA Connect Group LLC structures its import operations. It does not constitute regulatory advice or a contractual offer. Plant-level sanitary approvals belong in each case to the producers and processors at origin.',

    'compliance.certs.label': 'Certifications',
    'compliance.certs.title1': 'Internationally',
    'compliance.certs.titleItalic': 'Recognized',
    'compliance.certs.description': 'Our certifications are independently audited and renewed annually, reflecting our commitment to operational excellence.',
    'compliance.certs.status.active': 'Active',
    'compliance.certs.status.renewed': 'Renewed',
    'compliance.certs.issuedBy': 'Issued by',
    'compliance.certs.producerNote': 'The SGS and Bureau Veritas (Veritas Qualitas) certifications listed in this portal do not all belong to MERCALIA Connect Group LLC. In most cases, they correspond to our verified producers and suppliers, whose certifications support the quality and integrity of the commodities we trade.',

    'compliance.certs.0.name': 'Quality Management System',
    'compliance.certs.0.description': 'Certified quality management processes covering procurement, logistics coordination, and client service delivery across all commodity categories.',
    'compliance.certs.1.name': 'Environmental Management',
    'compliance.certs.1.description': 'Environmental stewardship embedded in our supply chain operations.',
    'compliance.certs.2.name': 'Occupational Health & Safety',
    'compliance.certs.2.description': 'Workplace safety standards applied to all operational facilities and partner sites.',
    'compliance.certs.3.name': 'Health & Safety Management',
    'compliance.certs.3.description': 'Comprehensive health and safety management across all operational touchpoints.',
    'compliance.certs.4.name': 'Supply Chain Security',
    'compliance.certs.4.description': 'End-to-end supply chain security management for high-value commodity shipments, ensuring integrity from origin to destination.',

    'compliance.reg.label': 'Regulatory Compliance',
    'compliance.reg.title1': 'Frameworks We',
    'compliance.reg.titleItalic': 'Operate Within',
    'compliance.reg.description': 'These are the frameworks we check every operation against: sanctions (OFAC), anti-money-laundering (AML), know-your-customer (KYC) and anti-bribery rules.',

    'compliance.quality.label': 'Quality Standards',
    'compliance.quality.title1': 'Precision at',
    'compliance.quality.titleItalic': 'Every Stage',
    'compliance.quality.description': 'Nuestro marco de gestión de calidad no es una casilla de verificación — es una disciplina operativa integrada en cada flujo de trabajo.',
    'compliance.quality.annualAudit.title': 'Annual Third-Party Quality Audits',
    'compliance.quality.annualAudit.description': 'All quality standards are independently verified by accredited auditing bodies on an annual basis, with interim reviews conducted quarterly.',
    'compliance.quality.0.title': 'Supplier Vetting Protocol',
    'compliance.quality.0.description': 'Cada proveedor undergoes a 47-point due diligence assessment covering financial stability, operational capacity, regulatory standing, and ethical practices before onboarding.',
    'compliance.quality.0.m0': 'Vetting Criteria',
    'compliance.quality.0.m1': 'Avg. Review Time',
    'compliance.quality.1.title': 'Product Quality Assurance',
    'compliance.quality.1.description': 'Inspecciones independientes de terceros en origen, tránsito y destino. Todas las materias primas se prueban contra benchmarks de calidad reconocidos internacionalmente antes de la aceptación.',
    'compliance.quality.1.m0': 'Inspection Points',
    'compliance.quality.1.m1': 'Rejection Rate',
    'compliance.quality.2.title': 'Documentation Integrity',
    'compliance.quality.2.description': 'Toda la documentación comercial — certificados de origen, certificados fitosanitarios, conocimientos de embarque y declaraciones aduaneras — se verifica para autenticidad y completitud.',
    'compliance.quality.2.m0': 'Document Types',
    'compliance.quality.2.m1': 'Verification Rate',
    'compliance.quality.3.title': 'Continuous Monitoring',
    'compliance.quality.3.description': 'Monitoreo de cumplimiento en tiempo real en todas las transacciones activas. Las alertas automatizadas señalan desviaciones de los umbrales regulatorios, permitiendo acciones correctivas inmediatas.',
    'compliance.quality.3.m0': 'Monitoring Frequency',
    'compliance.quality.3.m1': 'Alert Response',

    'compliance.audit.label': 'Audit Documentation',
    'compliance.audit.title1': 'Transparent',
    'compliance.audit.titleItalic': 'Audit Trail',
    'compliance.audit.note': 'Todos los informes de auditoría están disponibles para socios y clientes verificados previa solicitud.',
    'compliance.audit.requestReport': 'Request Full Report',
    'compliance.audit.status.passed': 'Passed',
    'compliance.audit.status.inReview': 'In Review',
    'compliance.audit.status.scheduled': 'Scheduled',
    'compliance.audit.scope': 'Scope',
    'compliance.audit.findings': 'Key Findings',
    'compliance.audit.summary.0.label': 'Audits Completed',
    'compliance.audit.summary.0.sub': 'This cycle',
    'compliance.audit.summary.1.label': 'Pass Rate',
    'compliance.audit.summary.1.sub': 'All completed audits',
    'compliance.audit.summary.2.label': 'Next Scheduled',
    'compliance.audit.summary.2.sub': 'GDPR Review',

    'compliance.audit.0.title': 'Annual ISO 9001 Quality Management Audit',
    'compliance.audit.0.findings': 'Zero non-conformities. 3 opportunities for improvement noted and actioned.',
    'compliance.audit.0.scope': 'All operational processes, supplier management, client service delivery',
    'compliance.audit.1.title': 'AML & KYC Compliance Review',
    'compliance.audit.1.findings': 'Cumplimiento total confirmado. Procedimientos de verificación mejorados implementados.',
    'compliance.audit.1.scope': 'Incorporación de clientes, monitoreo de transacciones, verificación de sanciones',
    'compliance.audit.2.title': 'Supply Chain Security Assessment',
    'compliance.audit.2.findings': 'Requisitos ISO 28000 completamente cumplidos. Sistemas de seguimiento digital elogiados.',
    'compliance.audit.2.scope': 'Cadena de suministro de extremo a extremo, socios logísticos, procedimientos aduaneros',
    'compliance.audit.3.title': 'Environmental Compliance Audit',
    'compliance.audit.3.findings': 'Auditoría en progreso. Hallazgos preliminares indican cumplimiento total.',
    'compliance.audit.3.scope': 'Sistema de gestión ambiental, huella de carbono, gestión de residuos',
    'compliance.audit.4.title': 'Data Protection & GDPR Audit',
    'compliance.audit.4.findings': 'Programada para junio 2025. Autoevaluación previa a la auditoría completada.',
    'compliance.audit.4.scope': 'Actividades de procesamiento de datos, gestión de consentimiento, derechos de los interesados',

    'compliance.cta.label': 'Partner With Confidence',
    'compliance.cta.title1': 'Compliance Is Not',
    'compliance.cta.titleItalic': 'Optional Here',
    'compliance.cta.description': 'Before we trade we verify our counterparty, and you can verify us: company registration, D-U-N-S, NOAA permit and BOI filing. Ask us for our corporate information sheet for your due diligence.',
    'compliance.cta.cta.dossier': 'Request Corporate Documents',
    'compliance.cta.cta.services': 'Explore Services',
    'compliance.cta.trust.0': 'Active NOAA permit',
    'compliance.cta.trust.1': 'Verifiable documents',
    'compliance.cta.trust.2': 'OFAC · AML · KYC screening',
  },
};

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  setLang: () => {},
  t: (key: string) => key,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>('en');

  // Idioma inicial: la elección guardada del visitante; si no hay, español cuando
  // el navegador está en español, para que vea nuestro texto y no la traducción
  // automática del navegador.
  useEffect(() => {
    let inicial: Language | null = null;
    try {
      const guardado = window.localStorage.getItem('mcg-lang');
      if (guardado === 'es' || guardado === 'en') inicial = guardado;
    } catch {}
    if (!inicial) {
      const principal = navigator.languages?.[0] ?? navigator.language ?? '';
      inicial = principal.toLowerCase().startsWith('es') ? 'es' : 'en';
    }
    setLangState(inicial);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (nuevo: Language) => {
    setLangState(nuevo);
    try {
      window.localStorage.setItem('mcg-lang', nuevo);
    } catch {}
  };

  const t = (key: string): string => {
    return translations[lang][key] ?? translations['en'][key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
